import {useEffect, useRef} from "react";

import {RegistrationPreferencesModal} from "../../../auth/components/registration_flow/RegistrationPreferencesModal";
import {
    trackUnlockChoiceExitButtonClick,
    trackUnlockChoiceModalView,
    trackUnlockChoicePreferencesSaved
} from "../../../auth/tracking/track_unlock_choice_events";
import {useHomeMatchBasicPreferencesFlow} from "../../hooks/use_home_match_basic_preferences_flow";
import {
    trackMultileadPreferencesChange,
    trackMultileadPreferencesClose,
    trackMultileadPreferencesSaved,
    trackMultileadPreferencesView
} from "../../tracking/amplitude/track_multilead_preferences_events";
import {IHomeMatchBasicPreferencesFlowAmplitudeItemContext, IHomeMatchBasicPreferencesFlowProps} from "../../types/HomeMatchBasicPreferencesFlowTypes";
import {IPreferencesDefaultValues} from "../../types/IPreferencesDefaultValues";
import {getHomeMatchBasicSurveyCopy} from "../../utils/home_match_basic_preferences_flow_utils";
import {HomeMatchBasicPreferencesFlowLoadingModal} from "./HomeMatchBasicPreferencesFlowLoadingModal";
import {HomeMatchBasicPreferencesFlowPhoneUpdateModal} from "./HomeMatchBasicPreferencesFlowPhoneUpdateModal";
import {HomeMatchBasicPreferencesFlowPhoneVerificationModal} from "./HomeMatchBasicPreferencesFlowPhoneVerificationModal";
import {HomeMatchBasicPreferencesFlowResignationModal} from "./HomeMatchBasicPreferencesFlowResignationModal";
import {HomeMatchBasicPreferencesFlowResignationResponsiveModal} from "./HomeMatchBasicPreferencesFlowResignationResponsiveModal";
import {HomeMatchBasicPreferencesFlowSummaryModal} from "./HomeMatchBasicPreferencesFlowSummaryModal";
import {HomeMatchBasicPreferencesFlowSummaryResponsiveModal} from "./HomeMatchBasicPreferencesFlowSummaryResponsiveModal";

export const HomeMatchBasicPreferencesFlow = (props: IHomeMatchBasicPreferencesFlowProps) => {
    const {
        isOpen,
        source,
        defaultHomeMatchBasicConsent,
        initialStep = "auto",
        propertyId,
        offerId,
        originalApplicationResponse,
        trackingContext,
        amplitudeTrackingContext,
        unlockChoiceTrackingContext,
        overlayVariant,
        onComplete,
        amplitudePlacementSource = "registrationFlow",
        summaryVariant = "modal",
        preferencesBasis = "offer",
        zIndex
    } = props;
    const amplitudeViewTrackedRef = useRef(false);
    const amplitudeSaveTrackedRef = useRef(false);
    const unlockChoiceViewTrackedRef = useRef(false);
    const unlockChoiceSaveTrackedRef = useRef(false);
    const unlockChoiceCloseTrackedRef = useRef(false);
    const flow = useHomeMatchBasicPreferencesFlow({
        isOpen,
        source,
        defaultHomeMatchBasicConsent,
        initialStep,
        propertyId,
        offerId,
        originalApplicationResponse,
        onComplete
    });

    useEffect(() => {
        if (!isOpen) {
            amplitudeViewTrackedRef.current = false;
            amplitudeSaveTrackedRef.current = false;
            unlockChoiceViewTrackedRef.current = false;
            unlockChoiceSaveTrackedRef.current = false;
            unlockChoiceCloseTrackedRef.current = false;
            return;
        }

        const isPreferencesStepShown = (flow.step === "summary" || flow.step === "survey") && !flow.isLoading;

        if (isPreferencesStepShown && unlockChoiceTrackingContext && !unlockChoiceViewTrackedRef.current) {
            unlockChoiceViewTrackedRef.current = true;
            trackUnlockChoiceModalView(unlockChoiceTrackingContext, "rp_preferences");
        }

        if (flow.step === "summary" && !flow.isLoading && amplitudeTrackingContext && !amplitudeViewTrackedRef.current) {
            amplitudeViewTrackedRef.current = true;
            trackMultileadPreferencesView({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }
    }, [
        amplitudeTrackingContext,
        unlockChoiceTrackingContext,
        flow.homeMatchBasicConsent,
        flow.isLoading,
        flow.regionDetails,
        flow.step,
        flow.summaryValues,
        isOpen
    ]);

    const trackUnlockChoicePreferencesSave = (values: IPreferencesDefaultValues) => {
        if (unlockChoiceTrackingContext && !unlockChoiceSaveTrackedRef.current) {
            unlockChoiceSaveTrackedRef.current = true;
            trackUnlockChoicePreferencesSaved(unlockChoiceTrackingContext, {
                values,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }
    };

    const trackUnlockChoicePreferencesClose = () => {
        if (unlockChoiceTrackingContext && !unlockChoiceSaveTrackedRef.current && !unlockChoiceCloseTrackedRef.current) {
            unlockChoiceCloseTrackedRef.current = true;
            trackUnlockChoiceExitButtonClick(unlockChoiceTrackingContext, "rp_preferences_close");
        }
    };

    // The first survey closes through a submit that keeps the values without the user confirming them
    const handleSurveySuccess = (values: IPreferencesDefaultValues, isExplicitSubmit: boolean) => {
        if (!flow.isEditingSurvey) {
            if (isExplicitSubmit) {
                trackUnlockChoicePreferencesSave(values);
            } else {
                trackUnlockChoicePreferencesClose();
            }
        }

        flow.handleSurveySuccess(values);
    };

    const handleEditPreferences = () => {
        if (amplitudeTrackingContext) {
            trackMultileadPreferencesChange(amplitudeTrackingContext);
        }

        flow.handleEditPreferences();
    };

    const handleSummaryConfirm = () => {
        if (amplitudeTrackingContext && !amplitudeSaveTrackedRef.current) {
            amplitudeSaveTrackedRef.current = true;
            trackMultileadPreferencesSaved({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }

        trackUnlockChoicePreferencesSave(flow.summaryValues);
        flow.handleSummaryConfirm();
    };

    const handleSummaryClose = () => {
        if (amplitudeTrackingContext && !amplitudeSaveTrackedRef.current) {
            trackMultileadPreferencesClose({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }

        trackUnlockChoicePreferencesClose();
        flow.handleSummaryClose();
    };

    if (!isOpen) {
        return null;
    }

    if (flow.step === "completed") {
        return null;
    }

    // With the data already cached the step resolves right after this render - a loader mounted now would be
    // unmounted before react-modal marks it as open, which leaves its body scroll lock behind for good
    if (flow.step === "resolving" && !flow.isStepResolutionPending) {
        return null;
    }

    if (flow.step === "resolving" || flow.isLoading || flow.isHomeMatchConfigurationLoading) {
        return <HomeMatchBasicPreferencesFlowLoadingModal isOpen={isOpen} overlayVariant={overlayVariant} zIndex={zIndex} />;
    }

    if (flow.step === "survey") {
        const surveyCopy = getHomeMatchBasicSurveyCopy(flow.isEditingSurvey);

        return (
            <RegistrationPreferencesModal
                isOpen={isOpen}
                isPropertyApplication={Boolean(propertyId)}
                propertyId={propertyId}
                offerId={offerId || undefined}
                application={originalApplicationResponse}
                surveySource={flow.surveySource}
                title={surveyCopy.title}
                description={surveyCopy.description}
                submitButtonText={flow.isEditingSurvey ? "ZAPISZ" : "ZAPISZ SWOJE PREFERENCJE"}
                closeButtonText={surveyCopy.closeButtonText}
                onCancel={flow.isEditingSurvey ? flow.handleSurveyCancel : undefined}
                overrideDefaultValues={flow.isEditingSurvey ? flow.summaryValues : undefined}
                onSuccess={handleSurveySuccess}
                showPriceHeader={!flow.isEditingSurvey}
                showCreditPlan={!flow.isEditingSurvey}
                layoutVariant={flow.isEditingSurvey ? "homeMatchBasicEdit" : "homeMatchBasicSurvey"}
                gtmEventCategory={flow.isEditingSurvey ? trackingContext?.eventCategory : undefined}
                gtmCustomLabel={flow.isEditingSurvey ? (trackingContext?.sourceModal ?? trackingContext?.eventName) : undefined}
                overlayVariant={overlayVariant}
                zIndex={zIndex}
            />
        );
    }

    if (flow.step === "resignation") {
        const amplitudeItemContext: IHomeMatchBasicPreferencesFlowAmplitudeItemContext = {
            itemId: amplitudeTrackingContext?.itemId ?? unlockChoiceTrackingContext?.itemId ?? offerId ?? null,
            propertyId: amplitudeTrackingContext?.propertyId ?? unlockChoiceTrackingContext?.propertyId ?? propertyId ?? null,
            ...(unlockChoiceTrackingContext && {authPlacement: unlockChoiceTrackingContext.authPlacement})
        };

        if (summaryVariant === "responsiveModal") {
            return (
                <HomeMatchBasicPreferencesFlowResignationResponsiveModal
                    isOpen={isOpen}
                    isSaving={flow.isSaving}
                    onDisable={() => flow.saveHomeMatchDecision(false, "stayModal")}
                    onKeepEnabled={flow.handleKeepEnabled}
                    onClose={flow.handleResignationClose}
                    trackingContext={trackingContext}
                    amplitudeItemContext={amplitudeItemContext}
                    zIndex={zIndex}
                />
            );
        }

        return (
            <HomeMatchBasicPreferencesFlowResignationModal
                isOpen={isOpen}
                isSaving={flow.isSaving}
                onDisable={() => flow.saveHomeMatchDecision(false, "stayModal")}
                onKeepEnabled={flow.handleKeepEnabled}
                onClose={flow.handleResignationClose}
                trackingContext={trackingContext}
                amplitudeItemContext={amplitudeItemContext}
                overlayVariant={overlayVariant}
                zIndex={zIndex}
            />
        );
    }

    if (flow.step === "phoneVerification") {
        return (
            <HomeMatchBasicPreferencesFlowPhoneVerificationModal
                isOpen={isOpen}
                onClose={flow.handlePhoneVerificationClose}
                onSuccess={flow.handlePhoneVerificationSuccess}
                onChangeNumber={flow.handlePhoneVerificationChangeNumber}
                overlayVariant={overlayVariant}
                amplitudePlacementSource={amplitudePlacementSource}
            />
        );
    }

    if (flow.step === "phoneUpdate") {
        return (
            <HomeMatchBasicPreferencesFlowPhoneUpdateModal
                isOpen={isOpen}
                onClose={flow.handlePhoneUpdateClose}
                onSuccess={flow.handlePhoneUpdateSuccess}
                overlayVariant={overlayVariant}
            />
        );
    }

    if (summaryVariant === "responsiveModal") {
        return (
            <HomeMatchBasicPreferencesFlowSummaryResponsiveModal
                isOpen={isOpen}
                values={flow.summaryValues}
                preferencesBasis={preferencesBasis}
                regionDetails={flow.regionDetails}
                homeMatchBasicConsent={flow.homeMatchBasicConsent}
                isSaving={flow.isSaving}
                onHomeMatchBasicConsentChange={flow.setHomeMatchBasicConsent}
                onEditPreferences={handleEditPreferences}
                onConfirm={handleSummaryConfirm}
                onClose={handleSummaryClose}
                trackingContext={trackingContext}
                zIndex={zIndex}
            />
        );
    }

    return (
        <HomeMatchBasicPreferencesFlowSummaryModal
            isOpen={isOpen}
            source={source}
            values={flow.summaryValues}
            regionDetails={flow.regionDetails}
            homeMatchBasicConsent={flow.homeMatchBasicConsent}
            isSaving={flow.isSaving}
            onHomeMatchBasicConsentChange={flow.setHomeMatchBasicConsent}
            onEditPreferences={handleEditPreferences}
            onConfirm={handleSummaryConfirm}
            onClose={handleSummaryClose}
            trackingContext={trackingContext}
            overlayVariant={overlayVariant}
            zIndex={zIndex}
        />
    );
};
import {useEffect, useRef} from "react";

import {RegistrationPreferencesModal} from "../../../auth/components/registration_flow/RegistrationPreferencesModal";
import {
    trackUnlockChoiceExitButtonClick,
    trackUnlockChoiceModalView,
    trackUnlockChoicePreferencesSaved
} from "../../../auth/tracking/track_unlock_choice_events";
import {useHomeMatchBasicPreferencesFlow} from "../../hooks/use_home_match_basic_preferences_flow";
import {
    trackMultileadPreferencesChange,
    trackMultileadPreferencesClose,
    trackMultileadPreferencesSaved,
    trackMultileadPreferencesView
} from "../../tracking/amplitude/track_multilead_preferences_events";
import {IHomeMatchBasicPreferencesFlowAmplitudeItemContext, IHomeMatchBasicPreferencesFlowProps} from "../../types/HomeMatchBasicPreferencesFlowTypes";
import {IPreferencesDefaultValues} from "../../types/IPreferencesDefaultValues";
import {getHomeMatchBasicSurveyCopy} from "../../utils/home_match_basic_preferences_flow_utils";
import {HomeMatchBasicPreferencesFlowLoadingModal} from "./HomeMatchBasicPreferencesFlowLoadingModal";
import {HomeMatchBasicPreferencesFlowPhoneUpdateModal} from "./HomeMatchBasicPreferencesFlowPhoneUpdateModal";
import {HomeMatchBasicPreferencesFlowPhoneVerificationModal} from "./HomeMatchBasicPreferencesFlowPhoneVerificationModal";
import {HomeMatchBasicPreferencesFlowResignationModal} from "./HomeMatchBasicPreferencesFlowResignationModal";
import {HomeMatchBasicPreferencesFlowResignationResponsiveModal} from "./HomeMatchBasicPreferencesFlowResignationResponsiveModal";
import {HomeMatchBasicPreferencesFlowSummaryModal} from "./HomeMatchBasicPreferencesFlowSummaryModal";
import {HomeMatchBasicPreferencesFlowSummaryResponsiveModal} from "./HomeMatchBasicPreferencesFlowSummaryResponsiveModal";

export const HomeMatchBasicPreferencesFlow = (props: IHomeMatchBasicPreferencesFlowProps) => {
    const {
        isOpen,
        source,
        defaultHomeMatchBasicConsent,
        initialStep = "auto",
        propertyId,
        offerId,
        originalApplicationResponse,
        trackingContext,
        amplitudeTrackingContext,
        unlockChoiceTrackingContext,
        overlayVariant,
        onComplete,
        amplitudePlacementSource = "registrationFlow",
        summaryVariant = "modal",
        preferencesBasis = "offer",
        zIndex
    } = props;
    const amplitudeViewTrackedRef = useRef(false);
    const amplitudeSaveTrackedRef = useRef(false);
    const unlockChoiceViewTrackedRef = useRef(false);
    const unlockChoiceSaveTrackedRef = useRef(false);
    const unlockChoiceCloseTrackedRef = useRef(false);
    const flow = useHomeMatchBasicPreferencesFlow({
        isOpen,
        source,
        defaultHomeMatchBasicConsent,
        initialStep,
        propertyId,
        offerId,
        originalApplicationResponse,
        onComplete
    });

    useEffect(() => {
        if (!isOpen) {
            amplitudeViewTrackedRef.current = false;
            amplitudeSaveTrackedRef.current = false;
            unlockChoiceViewTrackedRef.current = false;
            unlockChoiceSaveTrackedRef.current = false;
            unlockChoiceCloseTrackedRef.current = false;
            return;
        }

        const isPreferencesStepShown = (flow.step === "summary" || flow.step === "survey") && !flow.isLoading;

        if (isPreferencesStepShown && unlockChoiceTrackingContext && !unlockChoiceViewTrackedRef.current) {
            unlockChoiceViewTrackedRef.current = true;
            trackUnlockChoiceModalView(unlockChoiceTrackingContext, "rp_preferences");
        }

        if (flow.step === "summary" && !flow.isLoading && amplitudeTrackingContext && !amplitudeViewTrackedRef.current) {
            amplitudeViewTrackedRef.current = true;
            trackMultileadPreferencesView({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }
    }, [
        amplitudeTrackingContext,
        unlockChoiceTrackingContext,
        flow.homeMatchBasicConsent,
        flow.isLoading,
        flow.regionDetails,
        flow.step,
        flow.summaryValues,
        isOpen
    ]);

    const trackUnlockChoicePreferencesSave = (values: IPreferencesDefaultValues) => {
        if (unlockChoiceTrackingContext && !unlockChoiceSaveTrackedRef.current) {
            unlockChoiceSaveTrackedRef.current = true;
            trackUnlockChoicePreferencesSaved(unlockChoiceTrackingContext, {
                values,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }
    };

    const trackUnlockChoicePreferencesClose = () => {
        if (unlockChoiceTrackingContext && !unlockChoiceSaveTrackedRef.current && !unlockChoiceCloseTrackedRef.current) {
            unlockChoiceCloseTrackedRef.current = true;
            trackUnlockChoiceExitButtonClick(unlockChoiceTrackingContext, "rp_preferences_close");
        }
    };

    // The first survey closes through a submit that keeps the values without the user confirming them
    const handleSurveySuccess = (values: IPreferencesDefaultValues, isExplicitSubmit: boolean) => {
        if (!flow.isEditingSurvey) {
            if (isExplicitSubmit) {
                trackUnlockChoicePreferencesSave(values);
            } else {
                trackUnlockChoicePreferencesClose();
            }
        }

        flow.handleSurveySuccess(values);
    };

    const handleEditPreferences = () => {
        if (amplitudeTrackingContext) {
            trackMultileadPreferencesChange(amplitudeTrackingContext);
        }

        flow.handleEditPreferences();
    };

    const handleSummaryConfirm = () => {
        if (amplitudeTrackingContext && !amplitudeSaveTrackedRef.current) {
            amplitudeSaveTrackedRef.current = true;
            trackMultileadPreferencesSaved({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }

        trackUnlockChoicePreferencesSave(flow.summaryValues);
        flow.handleSummaryConfirm();
    };

    const handleSummaryClose = () => {
        if (amplitudeTrackingContext && !amplitudeSaveTrackedRef.current) {
            trackMultileadPreferencesClose({
                context: amplitudeTrackingContext,
                values: flow.summaryValues,
                autoMatchEnabled: flow.homeMatchBasicConsent,
                regionDetails: flow.regionDetails
            });
        }

        trackUnlockChoicePreferencesClose();
        flow.handleSummaryClose();
    };

    if (!isOpen) {
        return null;
    }

    if (flow.step === "completed") {
        return null;
    }

    // With the data already cached the step resolves right after this render - a loader mounted now would be
    // unmounted before react-modal marks it as open, which leaves its body scroll lock behind for good
    if (flow.step === "resolving" && !flow.isStepResolutionPending) {
        return null;
    }

    if (flow.step === "resolving" || flow.isLoading || flow.isHomeMatchConfigurationLoading) {
        return <HomeMatchBasicPreferencesFlowLoadingModal isOpen={isOpen} overlayVariant={overlayVariant} zIndex={zIndex} />;
    }

    if (flow.step === "survey") {
        const surveyCopy = getHomeMatchBasicSurveyCopy(flow.isEditingSurvey);

        return (
            <RegistrationPreferencesModal
                isOpen={isOpen}
                isPropertyApplication={Boolean(propertyId)}
                propertyId={propertyId}
                offerId={offerId || undefined}
                application={originalApplicationResponse}
                surveySource={flow.surveySource}
                title={surveyCopy.title}
                description={surveyCopy.description}
                submitButtonText={flow.isEditingSurvey ? "ZAPISZ" : "ZAPISZ SWOJE PREFERENCJE"}
                closeButtonText={surveyCopy.closeButtonText}
                onCancel={flow.isEditingSurvey ? flow.handleSurveyCancel : undefined}
                overrideDefaultValues={flow.isEditingSurvey ? flow.summaryValues : undefined}
                onSuccess={handleSurveySuccess}
                showPriceHeader={!flow.isEditingSurvey}
                showCreditPlan={!flow.isEditingSurvey}
                layoutVariant={flow.isEditingSurvey ? "homeMatchBasicEdit" : "homeMatchBasicSurvey"}
                gtmEventCategory={flow.isEditingSurvey ? trackingContext?.eventCategory : undefined}
                gtmCustomLabel={flow.isEditingSurvey ? (trackingContext?.sourceModal ?? trackingContext?.eventName) : undefined}
                overlayVariant={overlayVariant}
                zIndex={zIndex}
            />
        );
    }

    if (flow.step === "resignation") {
        const amplitudeItemContext: IHomeMatchBasicPreferencesFlowAmplitudeItemContext = {
            itemId: amplitudeTrackingContext?.itemId ?? unlockChoiceTrackingContext?.itemId ?? offerId ?? null,
            propertyId: amplitudeTrackingContext?.propertyId ?? unlockChoiceTrackingContext?.propertyId ?? propertyId ?? null,
            ...(unlockChoiceTrackingContext && {authPlacement: unlockChoiceTrackingContext.authPlacement})
        };

        if (summaryVariant === "responsiveModal") {
            return (
                <HomeMatchBasicPreferencesFlowResignationResponsiveModal
                    isOpen={isOpen}
                    isSaving={flow.isSaving}
                    onDisable={() => flow.saveHomeMatchDecision(false, "stayModal")}
                    onKeepEnabled={flow.handleKeepEnabled}
                    onClose={flow.handleResignationClose}
                    trackingContext={trackingContext}
                    amplitudeItemContext={amplitudeItemContext}
                    zIndex={zIndex}
                />
            );
        }

        return (
            <HomeMatchBasicPreferencesFlowResignationModal
                isOpen={isOpen}
                isSaving={flow.isSaving}
                onDisable={() => flow.saveHomeMatchDecision(false, "stayModal")}
                onKeepEnabled={flow.handleKeepEnabled}
                onClose={flow.handleResignationClose}
                trackingContext={trackingContext}
                amplitudeItemContext={amplitudeItemContext}
                overlayVariant={overlayVariant}
                zIndex={zIndex}
            />
        );
    }

    if (flow.step === "phoneVerification") {
        return (
            <HomeMatchBasicPreferencesFlowPhoneVerificationModal
                isOpen={isOpen}
                onClose={flow.handlePhoneVerificationClose}
                onSuccess={flow.handlePhoneVerificationSuccess}
                onChangeNumber={flow.handlePhoneVerificationChangeNumber}
                overlayVariant={overlayVariant}
                amplitudePlacementSource={amplitudePlacementSource}
            />
        );
    }

    if (flow.step === "phoneUpdate") {
        return (
            <HomeMatchBasicPreferencesFlowPhoneUpdateModal
                isOpen={isOpen}
                onClose={flow.handlePhoneUpdateClose}
                onSuccess={flow.handlePhoneUpdateSuccess}
                overlayVariant={overlayVariant}
            />
        );
    }

    if (summaryVariant === "responsiveModal") {
        return (
            <HomeMatchBasicPreferencesFlowSummaryResponsiveModal
                isOpen={isOpen}
                values={flow.summaryValues}
                preferencesBasis={preferencesBasis}
                regionDetails={flow.regionDetails}
                homeMatchBasicConsent={flow.homeMatchBasicConsent}
                isSaving={flow.isSaving}
                onHomeMatchBasicConsentChange={flow.setHomeMatchBasicConsent}
                onEditPreferences={handleEditPreferences}
                onConfirm={handleSummaryConfirm}
                onClose={handleSummaryClose}
                trackingContext={trackingContext}
                zIndex={zIndex}
            />
        );
    }

    return (
        <HomeMatchBasicPreferencesFlowSummaryModal
            isOpen={isOpen}
            source={source}
            values={flow.summaryValues}
            regionDetails={flow.regionDetails}
            homeMatchBasicConsent={flow.homeMatchBasicConsent}
            isSaving={flow.isSaving}
            onHomeMatchBasicConsentChange={flow.setHomeMatchBasicConsent}
            onEditPreferences={handleEditPreferences}
            onConfirm={handleSummaryConfirm}
            onClose={handleSummaryClose}
            trackingContext={trackingContext}
            overlayVariant={overlayVariant}
            zIndex={zIndex}
        />
    );
};
